import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Trophy,
  Flame,
  Users,
  ArrowLeft,
  Sparkles,
  Loader2,
  Gift,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import WeKitLogo from "@/components/WeKitLogo";
import { useAuth } from "@/contexts/AuthContext";
import {
  fetchXpLeaderboard,
  fetchReferralLeaderboard,
  type LeaderboardRow,
} from "@/lib/xp";

const RANK_STYLES: Record<number, string> = {
  1: "bg-gradient-to-br from-amber-400 to-amber-600 text-amber-50",
  2: "bg-gradient-to-br from-slate-300 to-slate-500 text-slate-50",
  3: "bg-gradient-to-br from-orange-400 to-orange-700 text-orange-50",
};

export default function Leaderboard() {
  const { user } = useAuth();
  const [xpRows, setXpRows] = useState<LeaderboardRow[] | null>(null);
  const [refRows, setRefRows] = useState<LeaderboardRow[] | null>(null);

  useEffect(() => {
    let active = true;
    Promise.all([fetchXpLeaderboard(50), fetchReferralLeaderboard(50)]).then(
      ([x, r]) => {
        if (!active) return;
        setXpRows(x);
        setRefRows(r);
      },
    );
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border/50 bg-background/95 backdrop-blur">
        <div className="flex items-center justify-between px-4 py-3 max-w-5xl mx-auto">
          <Link to="/dashboard" className="flex items-center gap-3 group">
            <ArrowLeft className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            <WeKitLogo size="sm" />
          </Link>
          <Badge variant="secondary" className="gap-1 text-xs">
            <Trophy className="h-3 w-3 text-primary" /> Leaderboards
          </Badge>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-foreground">
            Top <span className="gradient-text">Future Builders</span>
          </h1>
          <p className="mt-3 text-muted-foreground max-w-lg mx-auto">
            Real humans, real progress. Show up daily, invite friends, climb the
            ranks.
          </p>
        </motion.div>

        <Tabs defaultValue="xp" className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-md mx-auto mb-6">
            <TabsTrigger value="xp" className="gap-1.5">
              <Flame className="h-3.5 w-3.5" /> Top Builders
            </TabsTrigger>
            <TabsTrigger value="referrals" className="gap-1.5">
              <Users className="h-3.5 w-3.5" /> Top Inviters
            </TabsTrigger>
          </TabsList>

          <TabsContent value="xp">
            <Card className="border-border/50 bg-card/50 backdrop-blur">
              <CardContent className="p-3 sm:p-5">
                <LeaderboardList
                  rows={xpRows}
                  meKey={user?.id}
                  metricLabel="XP"
                  metric={(r) => r.total_xp ?? 0}
                  subtitle={(r) =>
                    r.current_streak
                      ? `🔥 ${r.current_streak}-day streak`
                      : undefined
                  }
                  emptyTitle="No XP yet"
                  emptyBody="Complete the assessment to earn your first 100 XP."
                  emptyCta={{ to: "/start", label: "Start assessment" }}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="referrals">
            <Card className="border-border/50 bg-card/50 backdrop-blur">
              <CardContent className="p-3 sm:p-5">
                <LeaderboardList
                  rows={refRows}
                  meKey={user?.id}
                  metricLabel="invited"
                  metric={(r) => r.converted_count ?? 0}
                  emptyTitle="No invites yet"
                  emptyBody="Share your link — help a friend discover their purpose."
                  emptyCta={{ to: "/refer", label: "Get your link" }}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button asChild variant="outline" className="gap-2">
            <Link to="/refer">
              <Gift className="h-4 w-4" /> Invite friends
            </Link>
          </Button>
          <Button asChild variant="ghost" className="gap-2">
            <Link to="/dashboard">
              <Sparkles className="h-4 w-4" /> Back to dashboard
            </Link>
          </Button>
        </div>
      </main>
    </div>
  );
}

function LeaderboardList({
  rows,
  meKey,
  metric,
  metricLabel,
  subtitle,
  emptyTitle,
  emptyBody,
  emptyCta,
}: {
  rows: LeaderboardRow[] | null;
  meKey?: string;
  metric: (r: LeaderboardRow) => number;
  metricLabel: string;
  subtitle?: (r: LeaderboardRow) => string | undefined;
  emptyTitle: string;
  emptyBody: string;
  emptyCta: { to: string; label: string };
}) {
  if (rows === null) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="text-center py-12 px-6">
        <p className="font-display text-lg font-semibold text-foreground">
          {emptyTitle}
        </p>
        <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto">
          {emptyBody}
        </p>
        <Button asChild className="mt-5">
          <Link to={emptyCta.to}>{emptyCta.label}</Link>
        </Button>
      </div>
    );
  }
  return (
    <ul className="divide-y divide-border/50">
      {rows.map((r) => {
        const isMe = meKey && r.user_id === meKey;
        return (
          <li
            key={r.user_id}
            className={`flex items-center gap-3 py-3 px-2 rounded-lg transition-colors ${
              isMe ? "bg-primary/5 ring-1 ring-primary/20" : ""
            }`}
          >
            <div
              className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                RANK_STYLES[r.rank] ?? "bg-muted text-muted-foreground"
              }`}
            >
              {r.rank}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-foreground truncate text-sm sm:text-base">
                {r.display_name}
                {isMe && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-primary font-bold">
                    you
                  </span>
                )}
              </p>
              {r.handle && (
                <p className="text-xs text-muted-foreground truncate">
                  @{r.handle}
                  {subtitle?.(r) && (
                    <span className="ml-2">· {subtitle(r)}</span>
                  )}
                </p>
              )}
            </div>
            <div className="text-right shrink-0">
              <p className="font-display font-bold text-foreground tabular-nums">
                {metric(r).toLocaleString()}
              </p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {metricLabel}
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
